const ROOT_SELECTOR = '[data-gw-card-carousel]';

function randomInt(maxExclusive) {
  if (maxExclusive <= 1) return 0;

  if (globalThis.crypto?.getRandomValues) {
    const range = 0x100000000;
    const limit = range - (range % maxExclusive);
    const value = new Uint32Array(1);

    do {
      globalThis.crypto.getRandomValues(value);
    } while (value[0] >= limit);

    return value[0] % maxExclusive;
  }

  return Math.floor(Math.random() * maxExclusive);
}

function shuffle(items) {
  for (let index = items.length - 1; index > 0; index -= 1) {
    const swapIndex = randomInt(index + 1);
    [items[index], items[swapIndex]] = [items[swapIndex], items[index]];
  }
  return items;
}

function getSlideshow(root) {
  return root?.querySelector(':scope > slideshow-component, :scope slideshow-component');
}

function getTrack(slideshow) {
  return slideshow?.querySelector(':scope > slideshow-container > slideshow-slides');
}

function getSlides(track) {
  return Array.from(track?.children || []).filter(
    (node) => node.tagName?.toLowerCase() === 'slideshow-slide'
  );
}

function getSourceSlides(root) {
  const template = root?.querySelector(':scope > template[data-gw-card-carousel-source]');
  const templateSlides = Array.from(template?.content?.children || []).filter(
    (node) => node.tagName?.toLowerCase() === 'slideshow-slide'
  );

  if (templateSlides.length) return templateSlides;

  return getSlides(getTrack(getSlideshow(root)));
}

function getLimit(root) {
  const raw = Number.parseInt(root.dataset.gwItemLimit || '0', 10) || 0;
  return raw > 0 ? raw : Number.POSITIVE_INFINITY;
}

function resetSlideState(slides) {
  slides.forEach((slide, index) => {
    slide.removeAttribute('hidden');
    slide.removeAttribute('reveal');
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
  });
}

function refreshSlideshow(slideshow) {
  if (!(slideshow instanceof HTMLElement)) return;

  const refreshed = slideshow.cloneNode(true);
  if (!(refreshed instanceof HTMLElement)) return;

  refreshed.setAttribute('initial-slide', '0');
  slideshow.replaceWith(refreshed);
}

function installSlides(root, sourceSlides, shouldShuffle) {
  const slideshow = getSlideshow(root);
  const track = getTrack(slideshow);
  if (!(slideshow instanceof HTMLElement) || !(track instanceof HTMLElement)) return false;

  let slides = sourceSlides.map((slide) => slide.cloneNode(true));
  if (shouldShuffle) slides = shuffle(slides);

  slides = slides.slice(0, getLimit(root));
  if (!slides.length) return false;

  resetSlideState(slides);
  track.replaceChildren(...slides);
  refreshSlideshow(slideshow);
  root.querySelector(':scope > template[data-gw-card-carousel-source]')?.remove();
  return true;
}

function normalizeExistingSlides(root, shouldShuffle = false) {
  installSlides(root, getSourceSlides(root), shouldShuffle);
}

async function loadRandomSource(root) {
  const sectionId = root.dataset.gwSourceSectionId || '';
  const totalItems = Math.max(
    0,
    Number.parseInt(root.dataset.gwRandomTotalItems || '0', 10) || 0
  );
  const pageSize = Math.max(
    1,
    Number.parseInt(root.dataset.gwRandomPageSize || root.dataset.gwItemLimit || '20', 10) || 20
  );

  if (!sectionId || totalItems <= pageSize) {
    normalizeExistingSlides(root, true);
    return;
  }

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const randomPage = 1 + randomInt(totalPages);

  try {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(randomPage));
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('_gw_card_carousel', Date.now().toString());

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) throw new Error('Card carousel source request failed');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const candidates = Array.from(doc.querySelectorAll(ROOT_SELECTOR));
    const sourceRoot =
      candidates.find((candidate) => candidate.dataset.gwSourceSectionId === sectionId) ||
      candidates[0];

    const sourceSlides = getSourceSlides(sourceRoot);

    if (!sourceSlides.length || !installSlides(root, sourceSlides, true)) {
      throw new Error('Card carousel source did not contain slides');
    }
  } catch (error) {
    normalizeExistingSlides(root, true);
  }
}

function notifyMobileProductCarouselReady(root) {
  if (window.innerWidth >= 750 || !window.location.pathname.startsWith('/products/')) return;

  const section = root.closest('.shopify-section') || root;
  const sectionId = root.dataset.gwSourceSectionId || section.id?.replace('shopify-section-', '') || '';

  requestAnimationFrame(() => {
    section.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId },
      })
    );
  });
}

async function initializeCardCarousel(root) {
  if (!(root instanceof HTMLElement)) return;
  if (root.dataset.gwCardCarouselReady === 'loading') return;
  if (root.dataset.gwCardCarouselReady === 'true') return;

  root.dataset.gwCardCarouselReady = 'loading';
  root.setAttribute('aria-busy', 'true');

  try {
    if (root.dataset.gwRandomize === 'true') {
      await loadRandomSource(root);
    } else {
      normalizeExistingSlides(root, true);
    }
  } finally {
    root.dataset.gwCardCarouselReady = 'true';
    root.setAttribute('aria-busy', 'false');
    notifyMobileProductCarouselReady(root);
  }
}

function initializeWithin(node) {
  if (!(node instanceof Element)) return;

  if (node.matches(ROOT_SELECTOR)) initializeCardCarousel(node);
  for (const root of node.querySelectorAll(ROOT_SELECTOR)) {
    initializeCardCarousel(root);
  }
}

function start() {
  initializeWithin(document.documentElement);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) initializeWithin(node);
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
